import axios from 'axios';
import React, { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import reducer from '../reducers/products_reducer';
import { products_client_url as url } from '../utils/constants';
import { getSecureUrl, getApiError } from '../utils/helpers';
import {
  SIDEBAR_OPEN,
  SIDEBAR_CLOSE,
  GET_PRODUCTS_BEGIN,
  GET_PRODUCTS_SUCCESS,
  GET_PRODUCTS_ERROR,
  GET_SINGLE_PRODUCT_BEGIN,
  GET_SINGLE_PRODUCT_SUCCESS,
  GET_SINGLE_PRODUCT_ERROR,
  GET_SINGLE_PRODUCT_REVIEWS_BEGIN,
  GET_SINGLE_PRODUCT_REVIEWS_ERROR,
  GET_SINGLE_PRODUCT_REVIEWS_SUCCESS,
} from '../actions';
import { useUserContext } from './user_context';

const initialState = {
  isSidebarOpen: false,
  products_loading: false,
  products_error: false,
  products: [],
  featured_products: [],
  single_product_loading: false,
  single_product_error: false,
  single_product: {},
  single_product_reviews_loading: false,
  single_product_reviews_error: false,
};

const ProductsContext = React.createContext();

export const ProductsProvider = ({ children }) => {
  const { currentUser } = useUserContext();
  const [state, dispatch] = useReducer(reducer, initialState);

  const openSidebar = () => {
    dispatch({ type: SIDEBAR_OPEN });
  };

  const closeSidebar = () => {
    dispatch({ type: SIDEBAR_CLOSE });
  };

  // Only the very first fetch should show the full-page loading/error state.
  // Background refreshes (tab refocus, reconnect, polling) update data silently
  // so the page doesn't appear to "reload" every time the user switches back to the tab.
  const hasFetchedOnceRef = useRef(false);

  const fetchProducts = useCallback(async (requestUrl = url, retries = 3) => {
    const isInitialLoad = !hasFetchedOnceRef.current;
    if (isInitialLoad) {
      dispatch({ type: GET_PRODUCTS_BEGIN });
    }

    for (let i = 0; i < retries; i++) {
      try {
        const separator = requestUrl.includes('?') ? '&' : '?';
        const response = await axios.get(`${requestUrl}${separator}_ts=${Date.now()}`);
        const products = response.data.data.map((product) => ({
          ...product,
          image: getSecureUrl(product.image),
          images: product.images
            ? product.images.map((img) => ({ ...img, url: getSecureUrl(img.url) }))
            : [],
          videos: product.videos
            ? product.videos.map((vid) => ({ ...vid, url: getSecureUrl(vid.url) }))
            : [],
          media: product.media
            ? product.media.map((m) => ({ ...m, url: getSecureUrl(m.url) }))
            : [],
        }));

        hasFetchedOnceRef.current = true;
        return dispatch({ type: GET_PRODUCTS_SUCCESS, payload: products }); // Exit early if successful
      } catch (error) {
        if (i === retries - 1) {
          // If this is the last retry, only show the full-page error on the initial load.
          // A failed background refresh just keeps showing the data already in memory.
          if (isInitialLoad) {
            dispatch({ type: GET_PRODUCTS_ERROR });
          }
        } else {
          // Exponential backoff: 2s, 4s before retrying
          await new Promise(resolve => setTimeout(resolve, 2000 * (i + 1)));
        }
      }
    }
  }, []);


  // const fetchProducts = async () => {
  //   dispatch({ type: GET_PRODUCTS_BEGIN });
  //   try {
  //     const response = await axios.get(url, {
  //       headers: {
  //         Authorization: "Bearer " + localStorage.getItem("acces_token"),
  //         ACCESS_ID:localStorage.getItem("access_id"),
  //         ISADMIN:localStorage.getItem("IsAdmin"),

  //       },
  //     });
  //     const { data } = response.data;
  //     dispatch({ type: GET_PRODUCTS_SUCCESS, payload: data });
  //   } catch (error) {
  //     dispatch({ type: GET_PRODUCTS_ERROR });
  //   }
  // };

  const fetchSingleProduct = async (url) => {
    dispatch({ type: GET_SINGLE_PRODUCT_BEGIN });
    try {
      const response = await axios.get(url);
      const singleProduct = response.data.data;
      if (singleProduct.image) {
        singleProduct.image = getSecureUrl(singleProduct.image);
      }
      if (singleProduct.images) {
        singleProduct.images = singleProduct.images.map((img) => ({
          ...img,
          url: getSecureUrl(img.url),
        }));
      }
      if (singleProduct.videos) {
        singleProduct.videos = singleProduct.videos.map((vid) => ({
          ...vid,
          url: getSecureUrl(vid.url),
        }));
      }
      if (singleProduct.media) {
        singleProduct.media = singleProduct.media.map((m) => ({
          ...m,
          url: getSecureUrl(m.url),
        }));
      }
      dispatch({
        type: GET_SINGLE_PRODUCT_SUCCESS,
        payload: singleProduct,
      });
    } catch (error) {
      dispatch({ type: GET_SINGLE_PRODUCT_ERROR });
    }
  };

  const getProductReviews = async (id) => {
    dispatch({ type: GET_SINGLE_PRODUCT_REVIEWS_BEGIN });
    try {
      const response = await axios.get(`${url}/reviews/${id}`);
      const reviews = response.data;
      dispatch({
        type: GET_SINGLE_PRODUCT_REVIEWS_SUCCESS,
        payload: reviews.data,
      });
    } catch (error) {
      dispatch({ type: GET_SINGLE_PRODUCT_REVIEWS_ERROR });
    }
  };

  const reviewProduct = async (id, stars, comment) => {
    if (currentUser) {
      const body = {
        name: currentUser.displayName || 'User',
        email: currentUser.email,
        rating: stars,
        comment: comment,
        productId: id,
      };
      try {
        const response = await axios.post(`${url}/reviews/`, body);
        getProductReviews(id);
        const { success, message } = response.data;
        return { success, message };
      } catch (error) {
        const { success, message } = getApiError(error);
        return { success, message };
      }
    }
  };

  useEffect(() => {
    fetchProducts();

    const handleFocus = () => {
      fetchProducts();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchProducts();
      }
    };

    // Auto-retry when network reconnects (fixes ERR_NETWORK_CHANGED)
    const handleOnline = () => {
      fetchProducts();
    };

    const refreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchProducts();
      }
    }, 60000);

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [fetchProducts]);

  return (
    <ProductsContext.Provider
      value={{
        ...state,
        openSidebar,
        closeSidebar,
        fetchProducts,
        fetchSingleProduct,
        reviewProduct,
        getProductReviews,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};
// make sure use
export const useProductsContext = () => {
  return useContext(ProductsContext);
};
